import { EventEmitter, Injectable } from '@angular/core';
import { waitSeconds } from '@reflact/tsutil';
import { ProjectKeyValEntry } from '@shared/ProjectKeyValStore';
import { keyValGetSchema, keyValSetSchema } from '@shared/ragai.schemas';
import dayjs from 'dayjs';
import { z } from 'zod';
import { EventManagerService } from './event-manager/event-manager.service';
import { SocketService } from './socket.service';

/**
 * Speichert nutzerspezifische, projektweite Schlüssel-Wert-Paare (`RAGAI.PROJECTKEYVAL`), geteilt über alle Bots eines Projekts.
 * @remarks
 * Strukturell identisch zu {@link KeyValService} (`RAGAI.KEYVAL`) - gleiche Methodennamen, gleiches
 * Synchronisationsprinzip per Socket-Events (`onKeyValUpdate`/`onKeyValDelete`). Der einzige Unterschied ist der
 * Geltungsbereich: `RAGAI.KEYVAL` ist pro Bot isoliert, `RAGAI.PROJECTKEYVAL` teilt die Daten eines Nutzers
 * über **alle Bots desselben Projekts** hinweg (z. B. für `RAGAI.GROUPCHAT` oder projektweite Nutzerprofile).
 * Es gibt hier keine `!`-Präfix-Konvention für dauerhafte Speicherung wie bei `RAGAI.KEYVAL`.
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectKeyValService {
  /** Feuert mit dem betroffenen Eintrag, sobald ein Wert per Socket von anderswo aktualisiert wurde. */
  public onKeyValUpdate: EventEmitter<ProjectKeyValEntry> = new EventEmitter<ProjectKeyValEntry>();
  /** Feuert mit dem betroffenen Eintrag, sobald ein Wert per Socket von anderswo gelöscht wurde. */
  public onKeyValDelete: EventEmitter<ProjectKeyValEntry> = new EventEmitter<ProjectKeyValEntry>();

  public keyValMap = new Map<string, ProjectKeyValEntry>();

  public initDone = false;

  constructor(
    private socketService: SocketService,
    private eventManager: EventManagerService
  ) {
    void this.init();
  }

  private getMapKey(key: string, type: string) {
    return `${type}-${key}`;
  }
  private getMapKeyByEntry(entry: ProjectKeyValEntry) {
    return this.getMapKey(entry.key, entry.type);
  }

  /** @internal Baut den internen Socket-Verbindungsaufbau und füllt `keyValMap` beim Start. */
  public async init() {
    const io = await this.socketService.getIo();
    io.on('projectKeyValUpdate', (entry: ProjectKeyValEntry) => {
      this.onKeyValUpdate.emit(entry);
      this.keyValMap.set(this.getMapKeyByEntry(entry), entry);
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.remoteUpdate", entry);
    });
    io.on('projectKeyValDelete', (entry: ProjectKeyValEntry) => {
      this.onKeyValDelete.emit(entry);
      this.keyValMap.delete(this.getMapKeyByEntry(entry));
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.remoteDelete", entry);
    });
    const items = await this.getInitialValues();
    for (const item of items) {
      this.keyValMap.set(this.getMapKeyByEntry(item), item);
    }
    this.initDone = true;
  }

  /**
   * @internal Wartet (busy-polling), bis die initiale Synchronisierung abgeschlossen ist.
   * @returns Immer `null`, sobald die Synchronisierung abgeschlossen ist.
   */
  public async waitForInit() {
    if (this.initDone) {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.waitForInit", 'ProjectKeyVal Init done');
      return;
    }
    await waitSeconds(0.1);
    await this.waitForInit();
    return null;
  }

  /**
   * @internal Lädt die initialen Einträge vom Server beim Start.
   * @returns Die initialen Einträge.
   */
  public async getInitialValues() {
    const io = await this.socketService.getIo();
    return new Promise<ProjectKeyValEntry[]>((resolve, _reject) => {
      io.emit('getInitialProjectKeyVals', (entries: ProjectKeyValEntry[]) => {
        resolve(entries);
      });
    });
  }

  /**
   * Löscht einen projektweiten Eintrag des aktuellen Nutzers.
   * @param key Der zu löschende Schlüssel, oder ein Objekt gemäß `keyValGetSchema`.
   * @param type Optionaler Typ zur Kategorisierung. Standard: `"custom"`.
   * @returns Der gelöschte Eintrag.
   */
  public async deleteKeyValEntry(key: string | z.input<typeof keyValGetSchema>, type: string = "custom"): Promise<unknown> {
    const parsed = keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      type = parsed.data.type;
      key = parsed.data.key;
    }
    const k = key as string;
    await this.waitForInit();
    const io = await this.socketService.getIo();
    return new Promise((resolve) => {
      io.emit('removeProjectKeyVal', { key: k, type }, (data: ProjectKeyValEntry) => {
        this.keyValMap.delete(this.getMapKey(k, type));
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.deleteKeyValEntry", data);
        resolve(data);
      });
    });
  }

  /**
   * @internal Low-Level-Implementierung von {@link set}; nutze stattdessen `set()`.
   * @param key Eindeutiger Schlüssel, oder ein Objekt gemäß `keyValSetSchema`.
   * @param value Der zu speichernde Wert.
   * @param type Optionaler Typ zur Kategorisierung.
   * @returns Der gespeicherte Eintrag.
   */
  public async storeKeyValEntry(key: string | z.input<typeof keyValSetSchema>, value?: unknown, type: string = "custom"): Promise<ProjectKeyValEntry> {
    const parsed = keyValSetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value ?? value;
      type = parsed.data.type;
      key = parsed.data.key;
    }
    const k = key as string;
    await this.waitForInit();
    const io = await this.socketService.getIo();
    return new Promise((resolve) => {
      io.emit('storeProjectKeyValUpdate', { key: k, value: value, type }, (data: ProjectKeyValEntry) => {
        this.keyValMap.set(this.getMapKey(k, type), data);
        this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.storeKeyValEntry", data);
        resolve(data);
      });
    });
  }

  /**
   * @internal Low-Level-Implementierung von {@link get}; nutze stattdessen `get()`.
   * @param key Eindeutiger Schlüssel, oder ein Objekt gemäß `keyValGetSchema`.
   * @param type Optionaler Typ zur Kategorisierung.
   * @returns Der gespeicherte Wert, oder `undefined`.
   */
  public async getKeyValEntry(key: string | z.input<typeof keyValGetSchema>, type: string = "custom"): Promise<unknown> {
    const parsed = keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      type = parsed.data.type;
      key = parsed.data.key;
    }
    const k = key as string;
    await this.waitForInit();
    const found = this.keyValMap.get(this.getMapKey(k, type));
    if (found) {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.getKeyValEntry", { key: k, value: found.value });
      return found.value;
    } else {
      return undefined;
    }
  }

  /**
   * @internal Low-Level-Implementierung von {@link getByType}; nutze stattdessen `getByType()`.
   * @param type Der gesuchte Typ.
   * @returns Liste der passenden Einträge.
   */
  public async getKeyValByType(type: string) {
    await this.waitForInit();
    const results: ProjectKeyValEntry[] = [];
    this.keyValMap.forEach((val, _key) => {
      if (val.type === type) {
        results.push(val);
      }
    });
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.PROJECTKEYVAL.getKeyValByType", results);
    return results;
  }

  /**
   * Gibt den Wert des angegebenen Schlüssels für den aktuellen Nutzer projektweit zurück, oder `null`.
   * @param key Eindeutiger Schlüssel, oder ein Objekt gemäß `keyValGetSchema`.
   * @param type Optionaler Typ zur Kategorisierung. Standard: `"custom"`.
   * @returns Der gespeicherte Wert oder `null`.
   */
  public async get(key: string | z.input<typeof keyValGetSchema>, type: string = "custom"): Promise<unknown> {
    const parsed = keyValGetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      type = parsed.data.type;
      key = parsed.data.key;
    }
    const val = (await this.getByType(type))
      .find(obj => obj.key === (key as string))
      ?.value ?? null;
    return val;
  }

  /**
   * Speichert einen Wert projektweit für den aktuellen Nutzer.
   * @param key Eindeutiger Schlüssel, oder ein Objekt gemäß `keyValSetSchema`.
   * @param value Der zu speichernde Wert.
   * @param type Optionaler Typ zur Kategorisierung. Standard: `"custom"`.
   * @returns Der gespeicherte Eintrag.
   */
  public async set(key: string | z.input<typeof keyValSetSchema>, value?: unknown, type: string = "custom"): Promise<ProjectKeyValEntry> {
    const parsed = keyValSetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value ?? value;
      type = parsed.data.type;
      key = parsed.data.key;
    }
    return await this.storeKeyValEntry(key, value, type);
  }
  /**
   * Wie {@link set}, hängt dem Schlüssel aber automatisch einen Zeitstempel an.
   * @param key Basis-Schlüssel, wird um einen Zeitstempel ergänzt, oder ein Objekt gemäß `keyValSetSchema`.
   * @param value Der zu speichernde Wert.
   * @param type Optionaler Typ zur Kategorisierung. Standard: `"custom"`.
   * @returns Der gespeicherte Eintrag.
   */
  public async unique(key: string | z.input<typeof keyValSetSchema>, value?: unknown, type: string = "custom"): Promise<ProjectKeyValEntry> {
    const parsed = keyValSetSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value ?? value;
      type = parsed.data.type;
      key = parsed.data.key;
    }
    return await this.storeKeyValEntry((key as string) + '_' + dayjs().toISOString(), value, type);
  }
  /**
   * Gibt alle projektweiten Einträge eines Typs für den aktuellen Nutzer zurück.
   * @param type Der gesuchte Typ.
   * @returns Liste der passenden Einträge.
   */
  public async getByType(type: string): Promise<ProjectKeyValEntry[]> { return this.getKeyValByType(type); }
}
